import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { SettingsContext } from "../../../SettingsContext";
import PhoneNumberInput from "../../../global/PhoneNumberInput";
import { addCustomerToOrganization, updateCustomer, fetchCustomerById } from "../../../../services/Http/Api";
import { useTranslation } from 'react-i18next';
import MainButton from "../../../../shared/layout/buttons/MainButton";
import { toast } from 'react-toastify';
import { SearchableSelect } from '../../../../shared/layout/forms/AppSelect';

const UserCreate = ({ onClose, onUserAdded, mode = 'create', customerId = null }) => {
    const { siteSettings } = useContext(SettingsContext);
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        phone_number: '',
        phone_country_id: '',
        country_id: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(mode === 'edit');

    // Load customer data when editing
    useEffect(() => {
        if (mode === 'edit' && customerId) {
            loadCustomerData();
        }
    }, [mode, customerId]);

    const loadCustomerData = async () => {
        try {
            setInitialLoading(true);
            const customer = await fetchCustomerById(customerId);
            setFormData({
                name: customer.user_name || customer.user?.name || '',
                surname: customer.user_surname || customer.user?.surname || '',
                email: customer.user_email || customer.user?.email || '',
                phone_number: customer.phone_number || customer.user?.phone_number || '',
                phone_country_id: customer.phone_country_id || customer.user?.phone_country_id || '',
                country_id: customer.country_id || customer.user?.country_id || customer.buyer_country_id || ''
            });
        } catch (error) {
            console.error('Failed to load customer:', error);
            toast.error(t('Users.UserCreate.loadError'));
        } finally {
            setInitialLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleCountryChange = (selectedOption) => {
        setFormData({ ...formData, country_id: selectedOption ? selectedOption.value : '' });
    };

    const handlePhoneNumberChange = ({ phoneCountryCode, phone }) => {
        setFormData({ ...formData, phone_country_id: phoneCountryCode, phone_number: phone });
    };

    const handleSubmit = async () => {
        setLoading(true);
        setErrors({});
        try {
            if (mode === 'edit') {
                await updateCustomer(customerId, formData);
                toast.success(t('Users.UserCreate.updateSuccess'));
            } else {
                await addCustomerToOrganization(formData);
                toast.success(t('Users.UserCreate.createSuccess'));
            }
            onUserAdded();
            onClose();
        } catch (error) {
            if (error.response && error.response.status === 422) {
                setErrors(error.response.data.errors);
                toast.error(t('Users.UserCreate.validationError'));
            } else {
                console.error('Failed to save customer:', error);
                toast.error(t('Users.UserCreate.saveError'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Prepare country options for select.
    const countryOptions = siteSettings?.countries?.map((country) => ({
        value: country.id,
        label: country.name,
        iso: country.iso
    })) || [];

    const selectedCountry = countryOptions.find(opt => opt.value === formData.country_id);

    // Custom option component with flag
    const CountryOption = ({ data, innerProps, isSelected }) => (
        <div {...innerProps} style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            backgroundColor: isSelected ? 'rgba(130, 22, 146, 0.1)' : 'transparent'
        }}>
            <img
                src={`https://flagcdn.com/24x18/${data.iso?.toLowerCase()}.png`}
                alt={data.label}
                style={{ width: 24, height: 18, marginRight: 10, borderRadius: 2 }}
                onError={(e) => e.target.style.display = 'none'}
            />
            <span>{data.label}</span>
        </div>
    );

    const SingleValue = ({ data }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
                src={`https://flagcdn.com/24x18/${data.iso?.toLowerCase()}.png`}
                alt={data.label}
                style={{ width: 24, height: 18, marginRight: 10, borderRadius: 2 }}
                onError={(e) => e.target.style.display = 'none'}
            />
            <span>{data.label}</span>
        </div>
    );

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '44px',
            borderColor: errors.country_id ? '#ef4444' : state.isFocused ? '#821692' : '#e5e7eb',
            boxShadow: state.isFocused ? '0 0 0 4px rgba(130, 22, 146, 0.1)' : 'none',
            backgroundColor: state.isFocused ? '#fff' : '#f9fafb',
            borderRadius: '8px',
            borderWidth: '1.5px',
            '&:hover': {
                borderColor: state.isFocused ? '#821692' : '#d1d5db'
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? 'rgba(130, 22, 146, 0.1)' : state.isFocused ? '#f3f4f6' : 'transparent',
            color: '#374151',
            cursor: 'pointer',
            '&:active': {
                backgroundColor: 'rgba(130, 22, 146, 0.15)'
            }
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af'
        })
    };

    if (initialLoading) {
        return (
            <LoadingContainer>
                <LoadingSpinner />
                <span>{t('Users.UserCreate.loading')}</span>
            </LoadingContainer>
        );
    }

    return (
        <StyledForm>
            {Object.keys(errors).length > 0 && (
                <ErrorAlert>{t('Users.UserCreate.errorAlert')}</ErrorAlert>
            )}

            <FormGrid>
                <FormGroup>
                    <Label>{t('Users.UserCreate.name')}</Label>
                    <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        isInvalid={!!errors.name}
                        placeholder={t('Users.UserCreate.namePlaceholder')}
                    />
                    {errors.name && <Feedback>{errors.name}</Feedback>}
                </FormGroup>

                <FormGroup>
                    <Label>{t('Users.UserCreate.surname')}</Label>
                    <Input
                        type="text"
                        name="surname"
                        value={formData.surname}
                        onChange={handleInputChange}
                        isInvalid={!!errors.surname}
                        placeholder={t('Users.UserCreate.surnamePlaceholder')}
                    />
                    {errors.surname && <Feedback>{errors.surname}</Feedback>}
                </FormGroup>

                <FormGroup>
                    <Label>{t('Users.UserCreate.email')}</Label>
                    <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        isInvalid={!!errors.email}
                        placeholder={t('Users.UserCreate.emailPlaceholder')}
                    />
                    {errors.email && <Feedback>{errors.email}</Feedback>}
                </FormGroup>

                <FormGroup>
                    <Label>{t('Users.UserCreate.country')}</Label>
                    <SearchableSelect
                        value={selectedCountry}
                        onChange={(_, __, option) => handleCountryChange(option)}
                        options={countryOptions}
                        placeholder={t('Users.UserCreate.selectCountry')}
                        isClearable
                        isSearchable
                        styles={customSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        components={{
                            Option: CountryOption,
                            SingleValue: SingleValue
                        }}
                    />
                    {errors.country_id && <Feedback>{errors.country_id}</Feedback>}
                </FormGroup>

                <FormGroup style={{ gridColumn: 'span 2' }}>
                    <Label>{t('Users.UserCreate.phoneNumber')}</Label>
                    <PhoneNumberInput
                        countryCodes={siteSettings?.countries || []}
                        phoneValue={formData.phone_number}
                        phoneCountryValue={formData.phone_country_id}
                        onChange={handlePhoneNumberChange}
                    />
                    {errors.phone_number && <Feedback>{errors.phone_number}</Feedback>}
                    {errors.phone_country_id && <Feedback>{errors.phone_country_id}</Feedback>}
                </FormGroup>
            </FormGrid>

            <ButtonContainer>
                <SecondaryButton type="button" onClick={onClose} disabled={loading}>
                    {t('Users.UserCreate.cancel')}
                </SecondaryButton>
                <MainButton type="button" onClick={handleSubmit} disabled={loading} style={{ minWidth: '120px' }}>
                    {loading ? t('Users.UserCreate.saving') : (mode === 'edit' ? t('Users.UserCreate.update') : t('Users.UserCreate.save'))}
                </MainButton>
            </ButtonContainer>

        </StyledForm>
    );
};

export default UserCreate;

// Styled Components
const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    box-sizing: border-box;
    width: 100%;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    box-sizing: border-box;
    width: 100%;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const ErrorAlert = styled.div`
    background-color: #fee2e2;
    color: #b91c1c;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #fecaca;
    margin-bottom: 0.5rem;
    box-sizing: border-box;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    box-sizing: border-box;
`;


const Label = styled.label`
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
`;

const Input = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 0.625rem 0.875rem;
    border: 1.5px solid ${({ isInvalid }) => (isInvalid ? '#ef4444' : '#e5e7eb')};
    border-radius: 8px;
    font-size: 0.9375rem;
    outline: none;
    transition: all 0.2s;
    background: #f9fafb;

    &:focus {
        border-color: #821692;
        background: #fff;
        box-shadow: 0 0 0 4px rgba(130, 22, 146, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const Feedback = styled.span`
    font-size: 0.75rem;
    color: #ef4444;
    font-weight: 500;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.625rem;
`;

const SecondaryButton = styled.button`
    background-color: #fff;
    color: #374151;
    border: 1px solid #e5e7eb;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #f9fafb;
        border-color: #d1d5db;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem;
    gap: 1rem;
    color: #6b7280;
`;

const LoadingSpinner = styled.div`
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #821692;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;
